import {ANALYSIS_OUTPUT_STEP_TYPES} from 'analysis-tool/AnalysisToolConstants';
import getLegacyStepFromAnalysis from 'analysis-tool/data/getStepFromAnalysis';
import {
  getIconForView,
  getTitleForView,
  isTopLevelAnalysisView,
} from 'analysis-tool/data-view/AnalysisViewUtils';
import {ResultsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {
  PipelineStepDataKind,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {readStepTypeInfosFromCache, StepTypeInfo} from 'pipelines/hooks/useStepTypeInfosQuery';
import {
  PipelineGraphQL,
  PipelineStep,
  PipelineStepRawData,
  PipelineViewGraphQL,
} from 'pipelines/PipelineTypes';
import {
  getChildViewsForView,
  getOutputDataForView,
  isChartView,
  isDataFrameView,
  resolveLineageStep,
} from 'pipelines/PipelineViewUtils';
import {pipelineStepRawDataIsOfKind} from 'pipelines/StepDataUtils';
import {getUserFacingStepTypeName} from 'pipelines/StepTypeInfoUtils';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {getPlateEdgeLabel, isScratchPlatesEnabled, isSendToDocumentEnabled} from './OutputsUtil';
import {
  AnalysisOutputConfigWithoutResultsMapping,
  AnalysisOutputOptionItem,
  AnalysisOutputType,
  BaseAnalysisOutputConfig,
} from './Types';

function isResultOptionEnabled(): boolean {
  return isFeatureEnabled({feature: ResultsFeature.RESULTS_CREATE});
}

interface PlateOutputReference {
  stepDataName: string;
  fromStepDataIndex: number;
}

/** Per-step `plateId -> source slot` map, precomputed once so option generation stays O(P), not O(P²). */
type PlateOutputReferenceMap = ReadonlyMap<string, ReadonlyMap<string, PlateOutputReference>>;

function buildPlateOutputReferenceMap(steps: ReadonlyArray<PipelineStep>): PlateOutputReferenceMap {
  const referencesByStepId = new Map<string, Map<string, PlateOutputReference>>();
  for (const step of steps) {
    const referencesByPlateId = new Map<string, PlateOutputReference>();
    step.outputStepDatas.forEach((stepData) => {
      stepData.rawDatas.forEach((rawData, fromStepDataIndex) => {
        // First slot containing the plate wins.
        if (
          pipelineStepRawDataIsOfKind(PipelineStepDataKind.PLATE, rawData) &&
          !referencesByPlateId.has(rawData.id)
        ) {
          referencesByPlateId.set(rawData.id, {stepDataName: stepData.name, fromStepDataIndex});
        }
      });
    });
    if (referencesByPlateId.size > 0) {
      referencesByStepId.set(step.id, referencesByPlateId);
    }
  }
  return referencesByStepId;
}

type GetOutputConfigFn<T extends AnalysisOutputType> = (
  baseOutputConfig: BaseAnalysisOutputConfig,
  view: PipelineViewGraphQL,
  rawData: PipelineStepRawData,
  steps: ReadonlyArray<PipelineStep>,
  plateReferenceMap: PlateOutputReferenceMap
) => Extract<AnalysisOutputConfigWithoutResultsMapping, {type: T}> | null;

/** Get AnalysisOutputType.DATASET output config, or null if not applicable to the view. */
const getDatasetOutputConfig: GetOutputConfigFn<AnalysisOutputType.DATASET> = (
  baseOutputConfig,
  view,
  rawData,
  steps
) =>
  isDataFrameView(view, steps) && pipelineStepRawDataIsOfKind(PipelineStepDataKind.DATASET, rawData)
    ? {...baseOutputConfig, type: AnalysisOutputType.DATASET, dataFrame: rawData}
    : null;

/** Get AnalysisOutputType.RESULT output config, or null if not applicable to the view. */
const getResultOutputConfig: GetOutputConfigFn<AnalysisOutputType.RESULT> = (
  baseOutputConfig,
  view,
  rawData,
  steps
) =>
  isDataFrameView(view, steps) &&
  pipelineStepRawDataIsOfKind(PipelineStepDataKind.DATASET, rawData) &&
  isResultOptionEnabled()
    ? {
        ...baseOutputConfig,
        type: AnalysisOutputType.RESULT,
        dataFrame: rawData,
        targetSpecificMappingState: null,
      }
    : null;

/** Get AnalysisOutputType.CHART output config, or null if not applicable to the view. */
const getChartOutputConfig: GetOutputConfigFn<AnalysisOutputType.CHART> = (
  baseOutputConfig,
  view,
  rawData,
  steps
) =>
  isChartView(view, steps) &&
  pipelineStepRawDataIsOfKind(PipelineStepDataKind.CHART, rawData) &&
  isSendToDocumentEnabled()
    ? {
        ...baseOutputConfig,
        type: AnalysisOutputType.CHART,
        chart: rawData,
      }
    : null;

/**
 * Get AnalysisOutputType.PLATE output config, or null if not applicable. Gated on both
 * send-to-document (plates are only output that way) and scratch plates.
 */
const getPlateOutputConfig: GetOutputConfigFn<AnalysisOutputType.PLATE> = (
  baseOutputConfig,
  _view,
  rawData,
  _steps,
  plateReferenceMap
) => {
  if (
    !(
      pipelineStepRawDataIsOfKind(PipelineStepDataKind.PLATE, rawData) &&
      isSendToDocumentEnabled() &&
      isScratchPlatesEnabled()
    )
  ) {
    return null;
  }
  const reference = plateReferenceMap.get(baseOutputConfig.step.id)?.get(rawData.id) ?? null;
  if (reference == null) {
    return null;
  }
  return {
    ...baseOutputConfig,
    type: AnalysisOutputType.PLATE,
    outputName: reference.stepDataName,
    fromStepDataName: reference.stepDataName,
    scratchPlates: [rawData],
    // Overwritten with the source step title + group count once options are collapsed by slot.
    edgeLabel: getPlateEdgeLabel('', reference.stepDataName, 1),
  };
};

/**
 * Collapse per-view plate options (one plate each) into one option per source-step output slot,
 * keyed by `${step.id}:${fromStepDataName}` and labeled "<step type>: <slot>, <N> Plates".
 */
function collapsePlateOptions(
  options: Array<Omit<AnalysisOutputOptionItem, 'selectorIndex'>>,
  stepTypeInfos: ReadonlyMap<PipelineStepType, StepTypeInfo>
): Array<Omit<AnalysisOutputOptionItem, 'selectorIndex'>> {
  const getPlateConfig = (
    option: Omit<AnalysisOutputOptionItem, 'selectorIndex'>
  ): Extract<AnalysisOutputConfigWithoutResultsMapping, {type: AnalysisOutputType.PLATE}> | null =>
    option.outputConfigs.length === 1 && option.outputConfigs[0].type === AnalysisOutputType.PLATE
      ? option.outputConfigs[0]
      : null;

  const result: Array<Omit<AnalysisOutputOptionItem, 'selectorIndex'>> = [];
  const groupResultIndexByKey = new Map<string, number>();
  const groupAccumByResultIndex = new Map<
    number,
    {
      baseOption: Omit<AnalysisOutputOptionItem, 'selectorIndex'>;
      config: Extract<AnalysisOutputConfigWithoutResultsMapping, {type: AnalysisOutputType.PLATE}>;
      scratchPlates: Array<
        Extract<
          AnalysisOutputConfigWithoutResultsMapping,
          {type: AnalysisOutputType.PLATE}
        >['scratchPlates'][number]
      >;
    }
  >();

  for (const option of options) {
    const plateConfig = getPlateConfig(option);
    if (plateConfig == null) {
      result.push(option);
      continue;
    }
    const key = `${plateConfig.step.id}:${plateConfig.fromStepDataName}`;
    const existingIndex = groupResultIndexByKey.get(key);
    if (existingIndex == null) {
      const resultIndex = result.length;
      groupResultIndexByKey.set(key, resultIndex);
      groupAccumByResultIndex.set(resultIndex, {
        baseOption: option,
        config: plateConfig,
        scratchPlates: [...plateConfig.scratchPlates],
      });
      result.push(option);
    } else {
      groupAccumByResultIndex.get(existingIndex)!.scratchPlates.push(...plateConfig.scratchPlates);
    }
  }

  for (const [resultIndex, accum] of groupAccumByResultIndex) {
    // Prefix by source step type, not the first plate's view title (which would name one plate).
    const stepTypeName = getUserFacingStepTypeName(accum.config.step.definition.stepType, stepTypeInfos);
    const edgeLabel = getPlateEdgeLabel(
      stepTypeName,
      accum.config.fromStepDataName,
      accum.scratchPlates.length
    );
    result[resultIndex] = {
      ...accum.baseOption,
      outputConfigs: [{...accum.config, scratchPlates: accum.scratchPlates, edgeLabel}],
      sidebarItemData: {...accum.baseOption.sidebarItemData, title: edgeLabel},
    };
  }

  return result;
}

/** Get all output configs that are applicable to a view. */
function getOutputConfigsForView(
  analysis: PipelineGraphQL,
  view: PipelineViewGraphQL,
  icon: BenchlingIconName,
  parentViewTitle: string | null,
  plateReferenceMap: PlateOutputReferenceMap
): Array<AnalysisOutputConfigWithoutResultsMapping> {
  const step = view.lineage[view.lineage.length - 1].step;
  const rawData = getOutputDataForView(view, analysis.steps);
  if (rawData == null) {
    return [];
  }

  const baseOutputConfig: BaseAnalysisOutputConfig = {
    icon,
    outputName: rawData.name,
    parentOutputName: parentViewTitle,
    step: getLegacyStepFromAnalysis(analysis, step.id),
  };

  return [getDatasetOutputConfig, getResultOutputConfig, getChartOutputConfig, getPlateOutputConfig]
    .map((getOutputConfigFn) =>
      getOutputConfigFn(baseOutputConfig, view, rawData, analysis.steps, plateReferenceMap)
    )
    .filter((outputConfig) => outputConfig != null);
}

/** Get all output options for a view, and its child views. */
function getOutputOptionsForViewAndChildViews(
  analysis: PipelineGraphQL,
  view: PipelineViewGraphQL,
  stepTypeInfos: ReadonlyMap<PipelineStepType, StepTypeInfo>,
  plateReferenceMap: PlateOutputReferenceMap,
  indentLevel: number = 0,
  parentViewTitle: string | null = null
): Array<Omit<AnalysisOutputOptionItem, 'selectorIndex'>> {
  const title = getTitleForView(view, analysis.steps, stepTypeInfos);
  const icon = getIconForView(analysis, view);
  const lastNode = view.lineage[view.lineage.length - 1];
  const lastStep = resolveLineageStep(analysis.steps, lastNode);
  const outputConfigs = getOutputConfigsForView(analysis, view, icon, parentViewTitle, plateReferenceMap);

  // Do not output the outputs of outputs
  if (ANALYSIS_OUTPUT_STEP_TYPES.find((stepType) => stepType === lastStep.stepType)) {
    return [];
  }

  const childOptionItems = getChildViewsForView(analysis, view)
    .filter((childView) => !isTopLevelAnalysisView(childView, analysis)) // Child views may themselves be top-level
    .flatMap((childView) =>
      getOutputOptionsForViewAndChildViews(
        analysis,
        childView,
        stepTypeInfos,
        plateReferenceMap,
        indentLevel + 1,
        title
      )
    );

  // Hide views that have neither config options nor a child with config options
  if (
    outputConfigs.length === 0 &&
    childOptionItems.length === 0 &&
    lastStep.statusCode !== PipelineStepStatusCode.RUNNING
  ) {
    return [];
  }

  return [
    {
      indentLevel,
      sidebarItemData: {icon, title},
      outputConfigs,
      disabledReason:
        outputConfigs.length > 0
          ? null
          : lastStep.statusCode === PipelineStepStatusCode.RUNNING
            ? 'This view is still running.'
            : 'This view cannot be output.',
    },
    ...childOptionItems,
  ];
}

/** Get all output options for an analysis, for use in `<AnalysisOutputSelector>`. */
export default function getViewBasedOutputOptions(
  analysis: PipelineGraphQL,
  stepTypeInfos: ReadonlyMap<PipelineStepType, StepTypeInfo> = readStepTypeInfosFromCache()
): Array<AnalysisOutputOptionItem> {
  const plateReferenceMap = buildPlateOutputReferenceMap(analysis.steps);
  return collapsePlateOptions(
    analysis.views
      .filter((view) => isTopLevelAnalysisView(view, analysis))
      .flatMap((view) =>
        getOutputOptionsForViewAndChildViews(analysis, view, stepTypeInfos, plateReferenceMap)
      ),
    stepTypeInfos
  ).map((option, selectorIndex) => ({...option, selectorIndex}));
}
